import { useEffect, useRef, useState } from "react";
import {
  BriefcaseIcon,
  FileTextIcon,
  PlusIcon,
  StickyNoteIcon,
  UserRoundIcon,
} from "lucide-react";

import { AddEnrichmentContextDialog } from "./AddEnrichmentContextDialog";
import { EnrichmentInputCard } from "./EnrichmentInputCard";
import { ProfilePreviewDialog } from "./ProfilePreviewDialog";
import { StafferCallsPreviewDialog } from "./StafferCallsPreviewDialog";
import { StevieCallsPreviewDialog } from "./StevieCallsPreviewDialog";
import { Column, Row } from "@/components";
import { CallIcon } from "@/components/CallIcon";
import { JobOverviewDialog } from "@jobs/JobOverviewDialog";
import { Button } from "@/components/ui/button";

import type { EnrichmentCandidacy } from "./types";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useGetCalls } from "@/hooks/queries/useGetCalls";
import { useGetVoiceAIOutreaches } from "@/hooks/queries/useGetVoiceAIOutreaches";
import { cn } from "@/lib/utils";
import { EnrichmentInputsState } from "@/types";

const ADD_BUTTON_CLASSES =
  "gap-2 rounded-full border border-dashed border-slate-300 bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700";

const formatCallSelectionDescription = ({
  isPending,
  selectedIds,
  total,
}: {
  isPending: boolean;
  selectedIds: Set<string>;
  total: number;
}): string => {
  if (isPending) return "Loading…";
  if (total === 0) return "No calls";
  return `${selectedIds.size.toString()} of ${total.toString()} selected`;
};

export const EnrichmentInputs = ({
  candidacy,
  enrichmentInputs,
}: {
  candidacy: EnrichmentCandidacy;
  enrichmentInputs: EnrichmentInputsState;
}) => {
  const {
    additionalContext,
    referenceCallTranscript,
    selectedStafferCallIds,
    selectedStevieCallIds,
    setAdditionalContext,
    setReferenceCallTranscript,
    setSelectedStafferCallIds,
    setSelectedStevieCallIds,
  } = enrichmentInputs;

  /* Contexts */
  const { companyName, job } = useSheetScope();

  /* Database CRUD Operations */
  // Reads
  const { data: stafferCalls, isPending: isStafferCallsPending } = useGetCalls({
    talentId: candidacy.talentId,
  });
  const { data: stevieCalls, isPending: isStevieCallsPending } =
    useGetVoiceAIOutreaches({
      currentCandidacyId: candidacy.candidacyId,
    });

  /* React Hooks */
  // useState
  const [isJobOverviewDialogOpen, setIsJobOverviewDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isStafferCallsDialogOpen, setIsStafferCallsDialogOpen] =
    useState(false);
  const [isStevieCallsDialogOpen, setIsStevieCallsDialogOpen] = useState(false);
  const [isReferenceCallDialogOpen, setIsReferenceCallDialogOpen] =
    useState(false);
  const [isAdditionalContextDialogOpen, setIsAdditionalContextDialogOpen] =
    useState(false);

  // Defaults the state sent to the BE to have all staffer and stevie calls selected to be used in Enrichment
  const stafferHydratedRef = useRef(false);
  const stevieHydratedRef = useRef(false);
  useEffect(() => {
    if (!stafferHydratedRef.current && stafferCalls) {
      setSelectedStafferCallIds(new Set(stafferCalls.map((call) => call.id)));
      stafferHydratedRef.current = true;
    }
  }, [stafferCalls, setSelectedStafferCallIds]);
  useEffect(() => {
    if (!stevieHydratedRef.current && stevieCalls) {
      setSelectedStevieCallIds(new Set(stevieCalls.map((call) => call.id)));
      stevieHydratedRef.current = true;
    }
  }, [stevieCalls, setSelectedStevieCallIds]);

  /* Helpers */
  const stafferCallsDescription = formatCallSelectionDescription({
    isPending: isStafferCallsPending,
    selectedIds: selectedStafferCallIds,
    total: stafferCalls?.length ?? 0,
  });
  const stevieCallsDescription = formatCallSelectionDescription({
    isPending: isStevieCallsPending,
    selectedIds: selectedStevieCallIds,
    total: stevieCalls?.length ?? 0,
  });
  const hasReferenceCall = referenceCallTranscript !== null;
  const hasAdditionalContext = additionalContext !== null;
  const visibleCardCount =
    4 + (hasReferenceCall ? 1 : 0) + (hasAdditionalContext ? 1 : 0);
  const gridSizeClasses =
    visibleCardCount === 6
      ? "max-w-[820px] sm:grid-cols-6"
      : visibleCardCount === 5
        ? "max-w-[690px] sm:grid-cols-5"
        : "max-w-[560px] sm:grid-cols-4";

  /* Render */
  const render = () => {
    return (
      <Column className="gap-4">
        <div
          className={cn(
            "mx-auto grid w-full grid-cols-2 gap-3",
            gridSizeClasses,
          )}
        >
          <EnrichmentInputCard
            description="Full job details"
            icon={<BriefcaseIcon />}
            iconClassName="bg-slate-100 text-slate-700"
            onClick={() => {
              setIsJobOverviewDialogOpen(true);
            }}
            title="Job overview"
          />
          <EnrichmentInputCard
            description="Resume and profile data"
            icon={<UserRoundIcon />}
            iconClassName="bg-primary-100 text-primary-900"
            onClick={() => {
              setIsProfileDialogOpen(true);
            }}
            title="Profile"
          />
          <EnrichmentInputCard
            description={stafferCallsDescription}
            icon={
              <CallIcon
                circleClassName="h-10 w-10 rounded-xl"
                className="h-4 w-4"
                hasTooltip={false}
              />
            }
            iconClassName="bg-transparent"
            onClick={() => {
              setIsStafferCallsDialogOpen(true);
            }}
            title="Staffer calls"
          />
          <EnrichmentInputCard
            description={stevieCallsDescription}
            icon={
              <CallIcon
                circleClassName="h-10 w-10 rounded-xl"
                className="h-4 w-4"
                hasTooltip={false}
                isAi
              />
            }
            iconClassName="bg-transparent"
            onClick={() => {
              setIsStevieCallsDialogOpen(true);
            }}
            title="Stevie calls"
          />
          {hasReferenceCall && (
            <EnrichmentInputCard
              description="Pasted transcript"
              icon={<FileTextIcon />}
              iconClassName="bg-sky-100 text-sky-700"
              onClick={() => {
                setIsReferenceCallDialogOpen(true);
              }}
              onRemove={() => {
                setReferenceCallTranscript(null);
              }}
              title="Reference call"
            />
          )}
          {hasAdditionalContext && (
            <EnrichmentInputCard
              description="Notes and extras"
              icon={<StickyNoteIcon />}
              iconClassName="bg-amber-100 text-amber-700"
              onClick={() => {
                setIsAdditionalContextDialogOpen(true);
              }}
              onRemove={() => {
                setAdditionalContext(null);
              }}
              title="Additional context"
            />
          )}
        </div>
        <Row className="flex-wrap justify-center gap-2">
          {!hasReferenceCall && (
            <Button
              className={ADD_BUTTON_CLASSES}
              onClick={() => {
                setIsReferenceCallDialogOpen(true);
              }}
              size="sm"
              variant="ghost"
            >
              <PlusIcon />
              Add reference call transcript
            </Button>
          )}
          {!hasAdditionalContext && (
            <Button
              className={ADD_BUTTON_CLASSES}
              onClick={() => {
                setIsAdditionalContextDialogOpen(true);
              }}
              size="sm"
              variant="ghost"
            >
              <PlusIcon />
              Add additional context
            </Button>
          )}
        </Row>

        <JobOverviewDialog
          companyName={companyName}
          job={job}
          onOpenChange={setIsJobOverviewDialogOpen}
          open={isJobOverviewDialogOpen}
        />
        <ProfilePreviewDialog
          onOpenChange={setIsProfileDialogOpen}
          open={isProfileDialogOpen}
          profile={candidacy.profile}
        />
        <StafferCallsPreviewDialog
          candidacy={candidacy}
          onOpenChange={setIsStafferCallsDialogOpen}
          open={isStafferCallsDialogOpen}
          selectedStafferCallIds={selectedStafferCallIds}
          setSelectedStafferCallIds={setSelectedStafferCallIds}
        />
        <StevieCallsPreviewDialog
          candidacy={candidacy}
          onOpenChange={setIsStevieCallsDialogOpen}
          open={isStevieCallsDialogOpen}
          selectedStevieCallIds={selectedStevieCallIds}
          setSelectedStevieCallIds={setSelectedStevieCallIds}
        />
        <AddEnrichmentContextDialog
          description="Paste a call transcript to include as additional context in the enrichment."
          initialValue={referenceCallTranscript ?? ""}
          onOpenChange={setIsReferenceCallDialogOpen}
          onSave={setReferenceCallTranscript}
          open={isReferenceCallDialogOpen}
          placeholder="Paste transcript…"
          saveButtonText="Include Reference"
          title="Reference call transcript"
        />
        <AddEnrichmentContextDialog
          description="Paste notes, prior feedback, or anything else relevant to include as additional context in the enrichment."
          initialValue={additionalContext ?? ""}
          onOpenChange={setIsAdditionalContextDialogOpen}
          onSave={setAdditionalContext}
          open={isAdditionalContextDialogOpen}
          placeholder="Anything else to include…"
          saveButtonText="Include Context"
          title="Additional context"
        />
      </Column>
    );
  };

  return render();
};
